import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, Users, MapPin, Clock, PartyPopper } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { api, apiError, prettyDate, WAIVER_URL } from "@/lib/api";

const toIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const emptyPassenger = () => ({ name: "", age: "" });

export default function BookingPage() {
  const [params, setParams] = useSearchParams();
  const [tours, setTours] = useState([]);
  const paramTour = params.get("tour");
  const tour = useMemo(() => tours.find((t) => t.id === paramTour) || tours[0], [tours, paramTour]);
  const tourId = tour?.id;
  const [availability, setAvailability] = useState([]);
  const [loadingAvail, setLoadingAvail] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [passengers, setPassengers] = useState([]);
  const [waiverAck, setWaiverAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    api.get("/tours").then((r) => setTours(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!tourId) return;
    setSelectedDate(null);
    setSelectedTime(null);
    setConfirmation(null);
    setLoadingAvail(true);
    api
      .get(`/availability/${tourId}`)
      .then((r) => setAvailability(r.data))
      .catch(() => setAvailability([]))
      .finally(() => setLoadingAvail(false));
  }, [tourId]);

  useEffect(() => {
    if (tour && passengers.length === 0) {
      setPassengers(Array.from({ length: tour.min_group }, emptyPassenger));
    }
  }, [tour]); // eslint-disable-line

  const daySlots = availability.filter((a) => a.date === selectedDate);
  const slot = daySlots.length === 1 ? daySlots[0] : daySlots.find((a) => a.time === selectedTime);

  const total = useMemo(() => {
    if (!tour) return 0;
    return passengers.reduce((sum, p) => {
      const age = parseInt(p.age, 10);
      if (tour.price_child != null && !isNaN(age) && age < 18) return sum + tour.price_child;
      return sum + tour.price_adult;
    }, 0);
  }, [passengers, tour]);

  const switchTour = (id) => {
    setParams({ tour: id });
    setPassengers([]);
    setWaiverAck(false);
  };

  const updatePassenger = (i, field, value) => {
    setPassengers((prev) => prev.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)));
  };

  const submit = async () => {
    if (!tour || !selectedDate) return toast.error("Please select a date");
    if (!slot) return toast.error("Please select a time slot");
    if (!customer.name.trim() || !customer.email.trim()) return toast.error("Please enter your name and email");
    if (!waiverAck) return toast.error("Please acknowledge the waiver requirement");
    for (const p of passengers) {
      if (!p.name.trim() || p.age === "") return toast.error("Please fill in the name and age of every guest");
      if (tour.min_age > 0 && parseInt(p.age, 10) < tour.min_age)
        return toast.error(`All guests must be ${tour.min_age}+ for the ${tour.name}`);
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        tour_id: tour.id,
        date: selectedDate,
        time: slot.time,
        customer_name: customer.name.trim(),
        email: customer.email.trim(),
        phone: customer.phone.trim(),
        passengers: passengers.map((p) => ({ name: p.name.trim(), age: parseInt(p.age, 10) })),
        waiver_ack: waiverAck,
      });
      setConfirmation(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div className="min-h-screen bg-[#f5fbfd] pt-28 pb-20 px-4">
        <div data-testid="confirmation-screen" className="max-w-2xl mx-auto bg-white rounded-3xl shadow-2xl border border-cyan-100 p-8 sm:p-12 text-center fade-up">
          <div className="mx-auto w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
            <CheckCircle2 size={44} className="text-emerald-500" />
          </div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 mb-2">You're booked! 🌴</h1>
          <p className="text-slate-600 mb-8">
            {confirmation.email_sent
              ? "A confirmation email is on its way to " + confirmation.email + "."
              : "Your booking is confirmed. Save your confirmation number below."}
          </p>
          <div className="bg-sky-50 border-2 border-dashed border-sky-300 rounded-2xl py-6 mb-8">
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Confirmation Number</p>
            <p data-testid="confirmation-number-badge" className="font-mono-code text-3xl font-bold text-sky-600 tracking-widest">
              {confirmation.code}
            </p>
          </div>
          <div className="text-left bg-slate-50 rounded-2xl p-6 mb-8 space-y-2 text-sm text-slate-700">
            <p><strong>Tour:</strong> {confirmation.tour_name}</p>
            <p><strong>Date:</strong> {prettyDate(confirmation.date)}</p>
            <p><strong>Time:</strong> {confirmation.time}</p>
            <p><strong>Guests:</strong> {confirmation.passengers.length}</p>
            <p><strong>Total due at arrival:</strong> ${confirmation.total.toFixed(2)}</p>
            <p className="pt-2 text-amber-700 bg-amber-50 rounded-lg p-3">
              Every guest must sign the{" "}
              <a href={WAIVER_URL} target="_blank" rel="noopener noreferrer" className="underline font-semibold">liability waiver</a>{" "}
              in person at boarding. Please arrive on time!
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/manage" data-testid="confirmation-manage-link" className="btn-ocean text-white font-bold px-6 py-3 rounded-full">
              Manage Booking
            </Link>
            <Link to="/" className="border border-slate-300 text-slate-700 font-bold px-6 py-3 rounded-full hover:bg-slate-50 transition-colors duration-150">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5fbfd] pt-28 pb-20 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mb-2">Book Your Adventure</h1>
        <p className="text-slate-600 mb-8">Pick your tour, choose a date, add your crew.</p>

        <div className="flex gap-3 mb-8 flex-wrap">
          {tours.map((t) => (
            <button
              key={t.id}
              data-testid={`tour-tab-${t.id}`}
              onClick={() => switchTour(t.id)}
              className={`flex items-center gap-2 px-5 py-3 rounded-full font-bold text-sm transition-colors duration-150 ${
                tourId === t.id ? "bg-sky-600 text-white shadow-lg" : "bg-white border border-slate-200 text-slate-600 hover:border-sky-300"
              }`}
            >
              {t.min_age > 0 ? <PartyPopper size={16} /> : <Users size={16} />} {t.name}
            </button>
          ))}
        </div>

        {tour && (
          <div className="bg-white rounded-3xl border border-cyan-100 shadow-xl p-6 sm:p-10 space-y-10">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2 text-slate-600"><MapPin size={16} className="text-cyan-500" /> {tour.location}</div>
              <div className="flex items-center gap-2 text-slate-600"><Clock size={16} className="text-cyan-500" /> {tour.boarding}</div>
              <div className="flex items-center gap-2 text-slate-600">
                <Users size={16} className="text-cyan-500" /> Min {tour.min_group} · Max {tour.capacity}
                {tour.min_age > 0 && ` · ${tour.min_age}+ only`}
              </div>
            </div>

            <div>
              <h2 className="font-display text-xl font-bold text-slate-900 mb-4">1. Choose a date</h2>
              {loadingAvail ? (
                <p className="text-slate-500 text-sm">Loading availability…</p>
              ) : availability.length === 0 ? (
                <p className="text-slate-500 text-sm bg-slate-50 rounded-xl p-4">
                  No dates currently available for this tour. Please check back soon!
                </p>
              ) : (
                <div data-testid="booking-date-picker" className="flex flex-col sm:flex-row gap-6 items-start">
                  <div className="rounded-2xl border-2 border-slate-200 bg-white p-2">
                    <Calendar
                      mode="single"
                      selected={selectedDate ? new Date(selectedDate + "T12:00:00") : undefined}
                      onSelect={(d) => {
                        if (d) {
                          setSelectedDate(toIso(d));
                          setSelectedTime(null);
                        }
                      }}
                      disabled={(d) => !availability.some((x) => x.date === toIso(d) && x.remaining > 0)}
                      modifiers={{ available: availability.filter((a) => a.remaining > 0).map((a) => new Date(a.date + "T12:00:00")) }}
                      modifiersClassNames={{ available: "day-available" }}
                      fromDate={new Date()}
                      toDate={new Date(availability[availability.length - 1].date + "T12:00:00")}
                    />
                    <p className="text-[11px] text-slate-500 px-3 pb-2 flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3 rounded-md bg-sky-100 border border-sky-400" /> Available dates
                    </p>
                  </div>
                  <div className="flex-1 w-full space-y-3">
                    {!selectedDate ? (
                      <p className="text-sm text-slate-500 bg-slate-50 rounded-2xl p-5">
                        Select a highlighted date on the calendar to see availability.
                      </p>
                    ) : (
                      <>
                        {daySlots.length > 1 && (
                          <div data-testid="time-slot-picker">
                            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Choose a time · 2-hour tour</p>
                            <div className="flex flex-col sm:flex-row gap-3">
                              {daySlots.map((s, idx) => (
                                <button
                                  key={s.time}
                                  data-testid={`time-option-${idx === 0 ? "morning" : "afternoon"}`}
                                  disabled={s.remaining === 0}
                                  onClick={() => setSelectedTime(s.time)}
                                  className={`date-chip flex-1 text-left rounded-2xl border-2 p-4 ${
                                    selectedTime === s.time
                                      ? "border-sky-500 bg-sky-50"
                                      : s.remaining === 0
                                      ? "border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed"
                                      : "border-slate-200 bg-white hover:border-sky-300"
                                  }`}
                                >
                                  <p className="font-bold text-slate-900 text-sm">{s.time}</p>
                                  <p className={`text-xs font-bold mt-1 ${s.remaining === 0 ? "text-red-500" : s.remaining <= 15 ? "text-orange-500" : "text-emerald-600"}`}>
                                    {s.remaining === 0 ? "Sold out" : `${s.remaining} spots left`}
                                  </p>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {slot && (
                          <div data-testid="booking-capacity-display" className="bg-sky-50 border border-sky-200 rounded-2xl p-5">
                            <p className="font-bold text-slate-900">{prettyDate(slot.date)}</p>
                            <p className="text-sm text-slate-600 mt-0.5">{slot.time}</p>
                            <p className={`text-sm font-bold mt-2 ${slot.remaining <= 15 ? "text-orange-500" : "text-emerald-600"}`}>
                              {slot.remaining} of {slot.capacity} spots available
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div>
              <h2 className="font-display text-xl font-bold text-slate-900 mb-4">2. Your contact details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <input
                  data-testid="customer-name-input"
                  className="border border-slate-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  placeholder="Full name"
                  value={customer.name}
                  onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                />
                <input
                  data-testid="customer-email-input"
                  type="email"
                  className="border border-slate-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  placeholder="Email address"
                  value={customer.email}
                  onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                />
                <input
                  data-testid="customer-phone-input"
                  className="border border-slate-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  placeholder="Phone (optional)"
                  value={customer.phone}
                  onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl font-bold text-slate-900">3. Guest list ({passengers.length})</h2>
                <button
                  data-testid="add-passenger-button"
                  onClick={() => setPassengers([...passengers, emptyPassenger()])}
                  disabled={slot && passengers.length >= slot.remaining}
                  className="flex items-center gap-1.5 text-sm font-bold text-sky-600 hover:text-sky-700 disabled:opacity-40"
                >
                  <Plus size={16} /> Add guest
                </button>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                {tour.min_group > 1 ? `Minimum ${tour.min_group} guests required` : "Groups of 1 to 50 welcome"}
                {tour.min_age > 0 ? ` · every guest must be ${tour.min_age} or older` : " · all ages welcome"}.
                {tour.price_child != null && ` Kids under 18: $${tour.price_child}.`}
              </p>
              <div className="space-y-3">
                {passengers.map((p, i) => (
                  <div key={i} className="flex gap-3 items-center">
                    <span className="text-xs font-bold text-slate-400 w-6">{i + 1}.</span>
                    <input
                      data-testid={`passenger-name-input-${i}`}
                      className="flex-1 min-w-0 border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                      placeholder="Guest full name"
                      value={p.name}
                      onChange={(e) => updatePassenger(i, "name", e.target.value)}
                    />
                    <input
                      data-testid={`passenger-age-input-${i}`}
                      type="number"
                      min="0"
                      max="120"
                      className="w-16 sm:w-24 shrink-0 border border-slate-300 rounded-xl px-2 sm:px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                      placeholder="Age"
                      value={p.age}
                      onChange={(e) => updatePassenger(i, "age", e.target.value)}
                    />
                    <button
                      data-testid={`remove-passenger-button-${i}`}
                      onClick={() => setPassengers(passengers.filter((_, idx) => idx !== i))}
                      disabled={passengers.length <= tour.min_group}
                      className="text-slate-400 hover:text-red-500 disabled:opacity-30 transition-colors duration-150"
                    >
                      <Trash2 size={17} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  data-testid="digital-waiver-checkbox"
                  type="checkbox"
                  checked={waiverAck}
                  onChange={(e) => setWaiverAck(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-amber-500"
                />
                <span className="text-sm text-slate-700">
                  I understand that <strong>every participant must sign the liability waiver in person at boarding</strong>.{" "}
                  <a href={WAIVER_URL} target="_blank" rel="noopener noreferrer" className="text-sky-600 underline font-semibold" onClick={(e) => e.stopPropagation()}>
                    Review the waiver (PDF)
                  </a>
                </span>
              </label>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-5 border-t border-slate-100 pt-7">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500">Total due at arrival</p>
                <p data-testid="booking-total-display" className="font-mono-code text-3xl font-bold text-slate-900">${total.toFixed(2)}</p>
                <p className="text-xs text-slate-500">Pay on arrival — no online payment needed</p>
              </div>
              <button
                data-testid="submit-booking-button"
                onClick={submit}
                disabled={submitting}
                className="btn-coral text-white font-bold px-10 py-4 rounded-full text-base disabled:opacity-60 w-full sm:w-auto"
              >
                {submitting ? "Booking…" : "Confirm Booking"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
